import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Loading from 'src/modules/shared/components/atoms/loading';
import { PiWarningCircle } from 'react-icons/pi';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  TCCredential,
  TCCredentialKind,
  type TCCredentialSecret,
  type TCCredentialRevealSecretResponse,
} from 'src/api/generated';
import { useRevealCredentialSecret } from 'src/modules/shared/hooks/api/credentials/credentials';

/**
 * Props for CredentialRevealSecretModal component.
 */
export interface CredentialRevealSecretModalProps {
  /** Credential whose secret is being revealed, or null when modal should be closed. */
  credential: TCCredential | null;
  /** Called when modal should be closed. */
  onClose: () => void;
}

/**
 * CredentialRevealSecretModal component.
 *
 * @remarks
 * This modal fetches and displays sensitive secret data. It should only be reachable for super admins.
 */
export const CredentialRevealSecretModal: React.FC<
  CredentialRevealSecretModalProps
> = ({ credential, onClose }) => {
  const { t } = useI18n();
  const isOpen = !!credential;

  const { revealCredentialSecret, loading, error } =
    useRevealCredentialSecret();
  const [resolved, setResolved] =
    useState<TCCredentialRevealSecretResponse | null>(null);

  const isApiKeySecret = (
    secret: TCCredentialSecret
  ): secret is { apiKey: string } =>
    typeof (secret as { apiKey?: unknown }).apiKey === 'string';

  const isBearerSecret = (
    secret: TCCredentialSecret
  ): secret is { token: string } =>
    typeof (secret as { token?: unknown }).token === 'string';

  const isBasicSecret = (
    secret: TCCredentialSecret
  ): secret is { username: string; password: string } =>
    typeof (secret as { username?: unknown }).username === 'string' &&
    typeof (secret as { password?: unknown }).password === 'string';

  useEffect(() => {
    let cancelled = false;

    const fetchSecret = async () => {
      if (!credential?.credentialId) return;
      setResolved(null);
      const data = await revealCredentialSecret(credential.credentialId);
      if (cancelled) return;
      setResolved(data);
    };

    if (credential) {
      void fetchSecret();
    }

    return () => {
      cancelled = true;
    };
  }, [credential, revealCredentialSecret]);

  const secretInputs = useMemo(() => {
    if (!resolved) return null;

    switch (resolved.kind) {
      case TCCredentialKind.API_KEY:
        if (!isApiKeySecret(resolved.secret)) return null;
        return (
          <LabelTextInput
            label={t('settings.credentials.form.apiKey')}
            labelPosition="top"
            inputProps={{
              value: resolved.secret.apiKey,
              readOnly: true,
            }}
          />
        );
      case TCCredentialKind.BEARER:
        if (!isBearerSecret(resolved.secret)) return null;
        return (
          <LabelTextInput
            label={t('settings.credentials.form.bearerToken')}
            labelPosition="top"
            inputProps={{
              value: resolved.secret.token,
              readOnly: true,
            }}
          />
        );
      case TCCredentialKind.BASIC:
        if (!isBasicSecret(resolved.secret)) return null;
        return (
          <div className="space-y-3">
            <LabelTextInput
              label={t('settings.credentials.form.username')}
              labelPosition="top"
              inputProps={{
                value: resolved.secret.username,
                readOnly: true,
              }}
            />
            <LabelTextInput
              label={t('settings.credentials.form.password')}
              labelPosition="top"
              inputProps={{
                value: resolved.secret.password,
                readOnly: true,
              }}
            />
          </div>
        );
      case TCCredentialKind.CUSTOM:
        return (
          <LabelTextArea
            label={t('settings.credentials.form.secretJson')}
            labelPosition="top"
            inputProps={{
              value: JSON.stringify(resolved.secret, null, 2),
              readOnly: true,
              rows: 8,
            }}
          />
        );
      default:
        return null;
    }
  }, [resolved, t]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Text size="h6" weight="semibold">
          {t('settings.credentials.reveal.title')}
        </Text>
      }
      body={
        credential ? (
          <div className="w-full space-y-3">
            <Text size="small" color="secondary">
              {credential.name ?? '-'} ({credential.provider ?? '-'})
            </Text>
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Loading size="md" />
              </div>
            )}
            {!loading && error && (
              <Text size="small" color="danger">
                {t('settings.credentials.alerts.error')}
              </Text>
            )}
            {!loading && !error && resolved && (
              <div className="space-y-3">
                {secretInputs}
                <Alert
                  variant="warning"
                  appearance="soft"
                  size="sm"
                  icon={<PiWarningCircle className="w-4 h-4" />}
                  message={t('settings.credentials.reveal.warning')}
                />
              </div>
            )}
          </div>
        ) : null
      }
      footer={
        <div className="flex justify-end gap-3 px-4 py-3">
          <Button variant="primary" onClick={onClose} disabled={loading}>
            {t('settings.credentials.actions.close')}
          </Button>
        </div>
      }
      dialogClassName="max-w-lg"
    />
  );
};
